

// Probably need an ID gotten off the route, useParams mayhaps?

import { Box, MenuItem, Paper, styled, TextField, Typography, useTheme } from "@mui/material";
import { gridClasses, GridColDef, GridRenderEditCellParams, useGridApiContext } from "@mui/x-data-grid";
import { GetFieldLookup, GetFieldLookupItem, ILookup, useFormMetadata, useFormSave, useLookups } from "@ngt/forms";
import { FC, useContext, useMemo } from "react";
import { Outlet, useParams } from "react-router-dom";
import { FormDefinitions, ProjectPrecinct } from "../api/dtos";
import CollapsibleTablePremium from "../components/tables/CollapsibleTablePremium";
import TitleBox from "../components/TitleBox";
import { createCustomToolbarProps } from "../components/utility/CustomToolbarProps";
import ProjectContext from "../contexts/data/ProjectContext";
import useProjectPrecinctsByProjectId from "../hooks/data/useProjectPrecinctsByProjectId";

const StyledPaper = styled(Paper) (({theme}) => ({
    marginBottom: theme.spacing(2)
}));

interface GridLookupParams extends GridRenderEditCellParams {
    lookups?: ILookup[]
}

const DataGridSelect:FC<GridLookupParams> = (props) => {
    const theme = useTheme();

    const apiRef = useGridApiContext();

    const handleValueChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = +event.target.value;
        apiRef.current.setEditCellValue({ id: props.id, field: props.field, value: newValue });
    };

    return (
        <TextField
                select
                value={props.value}
                fullWidth
                variant="standard"
                slotProps={{
                    input: {
                        disableUnderline: true
                    }
                }}
                sx={{
                    alignSelf: 'center',
                    flex: '1 1 auto',
                    justifyContent: 'stretch',
                }}
                onChange={handleValueChange}
            >
                {GetFieldLookup(props.lookups, "PrecinctApproval")?.items?.map((x, i) => (
                    <MenuItem 
                        key={i.toString()} 
                        value={x.id?.toString() ?? ""}
                        sx={{
                            paddingLeft: theme.spacing(1)
                        }}
                    >
                        {x.value?.toString()}
                    </MenuItem>
                ))}
        </TextField>
    );
}

const useProjectPrecinctColumns = () => {

    const metadata = useFormMetadata(FormDefinitions.ProjectPrecinct);

    const {data: lookups, loading: _lookupsLoading} = useLookups(metadata);

    const projectPrecinctColumns = useMemo(() => {
        const columns: GridColDef<ProjectPrecinct>[] =  [
            {
                field: "name",
                headerName: "Name",
                editable: true,
                flex: 1
            },
            {
                field: "sageCode",
                headerName: "Sage Code",
                editable: true,
                flex: 1
            },
            {
                field: "precinctApproval",
                headerName: "Precinct Approval",
                renderCell: (params) => GetFieldLookupItem(lookups, 'PrecinctApproval', params.row.precinctApproval)?.value?.toString(),
                editable: true,
                renderEditCell: (params: GridRenderEditCellParams) => (
                    <DataGridSelect
                        {...params?? {}}
                        lookups={lookups ?? []}
                        value={params.value ? params.value : ""} 
                    />
                ),
                flex: 1
            }
        ];

        return columns;
    }, [lookups]);

    return projectPrecinctColumns;
}

export const BulkEditProjectPrecincts: FC = () => {
    const projectPrecinctMetadata= useFormMetadata(FormDefinitions.ProjectPrecinct);

    const save = useFormSave(projectPrecinctMetadata);

    const theme = useTheme();
    
    const {data: _lotLookups, loading: _lookupsLoading} = useLookups(projectPrecinctMetadata);
    
    const params = useParams();
    
    const projectId = Number(params.projectId);
        
    const {data: projectPrecincts, loading: projectPrecinctsLoading} = useProjectPrecinctsByProjectId(projectId);

    const submit = (newProjectPrecinct: ProjectPrecinct) => {
        save(newProjectPrecinct);
        return newProjectPrecinct;
    }

    const {data: project} = useContext(ProjectContext);
    
    const customToolbarProps = useMemo(() => createCustomToolbarProps({
        exportTitle: `${project?.name} - Precincts`,
        fileName: `${project?.name} - Precincts`,
        includeDateTime: true
    }), [project]);   
    
    const columns = useProjectPrecinctColumns();

    return (

        //Properties

        //Summary

        //StageList
        <>
            <Outlet />

            <TitleBox>
                <Typography
                    variant="h1"
                >
                    {/*projectData?.project?.name*/}
                </Typography>
            </TitleBox>

            <StyledPaper>
                <Box
                    sx={{
                        [`& .${gridClasses.root}`]: {
                            
                            [`& .${gridClasses.columnHeader}`]: {
                                height: 'auto !important',
                                minHeight: 56,
                                
                                [`& .${gridClasses.columnHeaderTitleContainer}`]: {
                                    whiteSpace: 'normal',
                                    
                                    [`& .${gridClasses.columnHeaderTitle}`]: {
                                        whiteSpace: 'normal',
                                        paddingTop: theme.spacing(1.5),
                                        paddingBottom: theme.spacing(1.5) 
                                    }
                                },
                                
                                [`& .${gridClasses.iconButtonContainer}`]: {
                                    width: 'unset',
                                },
                                
                                '&.column-group-header': {
                                    backgroundColor: theme.palette.grey[300],
                                    boxShadow: "1",
                                    textAlign: 'center'
                                }
                            }
                        },
                    }}
                    >
                    <CollapsibleTablePremium
                        density="compact"
                        editMode="row"
                        processRowUpdate={(newRow, _oldRow, _params) => submit(newRow)}
                        //onRowEditStop={(params, event) => lotEditSubmit(params, event)}
                        startCollapsed={false} 
                        title={"Project Precincts"} 
                        loading={projectPrecinctsLoading} 
                        columns={columns}
                        rows={projectPrecincts}
                        disableColumnFilter={false}
                        {...customToolbarProps}
                    />
                </Box>
            </StyledPaper>
        </>
    );
}